import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import type { Equipment, ServiceRecord, ServiceRecordPartLine } from '../../types/equipment';
import type { Part } from '../../types/parts';
import partsService from '../../services/parts';
import { Wrench, Plus, CheckCircle, AlertTriangle, Calendar, Gauge, Package, Trash2, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '@/utils/timezone';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  scheduled:   'Scheduled',
  in_progress: 'In Progress',
  completed:   'Completed',
  cancelled:   'Cancelled',
};

const STATUS_COLOUR: Record<string, string> = {
  scheduled:   'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed:   'bg-green-100 text-green-700',
  cancelled:   'bg-neutral-100 text-neutral-500',
};

// ─── Create Form ──────────────────────────────────────────────────────────────

interface CreateFormProps {
  equipment: Equipment;
  onSave: (data: Partial<ServiceRecord>) => Promise<void>;
  onCancel: () => void;
}

function CreateForm({ equipment, onSave, onCancel }: CreateFormProps) {
  const [title, setTitle]         = useState('');
  const [description, setDesc]    = useState('');
  const [scheduledDate, setSched] = useState('');
  const [hoursAt, setHoursAt]     = useState(equipment.current_hours?.toString() ?? '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        scheduled_date: scheduledDate || undefined,
        hours_at_service: hoursAt ? parseFloat(hoursAt) : undefined,
      });
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />{error}
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Title *</label>
        <input
          type="text" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Annual service, Oil change"
          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Description</label>
        <textarea
          rows={2} value={description} onChange={e => setDesc(e.target.value)}
          placeholder="Work to be carried out…"
          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-neutral-500 mb-1">Scheduled date</label>
          <input
            type="date" value={scheduledDate} onChange={e => setSched(e.target.value)}
            className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        {(equipment.meter_type === 'hours' || equipment.meter_type === 'both') && (
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Hours at service</label>
            <input
              type="number" min="0" step="0.1" value={hoursAt} onChange={e => setHoursAt(e.target.value)}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button isLoading={saving} type="submit" className="flex-1">
          <CheckCircle className="h-4 w-4 mr-1.5" />
          Create Record
        </Button>
      </div>
    </form>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ServiceRecords() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAccess: canManage } = useModuleLicense('vehicles');
  const eqId = parseInt(id!);

  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [records, setRecords]     = useState<ServiceRecord[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);

  useEffect(() => {
    Promise.all([
      equipmentService.getById(eqId),
      equipmentService.getServices(eqId),
    ]).then(([eqRes, srRes]) => {
      if (eqRes.success && eqRes.data) setEquipment(eqRes.data);
      if (srRes.success && srRes.data) setRecords(srRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eqId]);

  async function handleCreate(data: Partial<ServiceRecord>) {
    const res = await equipmentService.createService(eqId, data as Parameters<typeof equipmentService.createService>[1]);
    if (res.success && res.data) {
      setRecords(prev => [res.data!, ...prev]);
      setShowForm(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Service Records" />
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse border border-neutral-100" />)}
        </div>
      </div>
    );
  }

  const upcoming  = records.filter(r => r.status === 'scheduled' || r.status === 'in_progress');
  const completed = records.filter(r => r.status === 'completed' || r.status === 'cancelled');

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Service Records"
        subtitle={equipment?.name ?? ''}
        actionButtons={canManage ? (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Record
          </Button>
        ) : undefined}
      />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Create form */}
        {showForm && equipment && (
          <Card>
            <CardContent className="pt-4">
              <p className="font-semibold text-neutral-800 mb-3">New Service Record</p>
              <CreateForm
                equipment={equipment}
                onSave={handleCreate}
                onCancel={() => setShowForm(false)}
              />
            </CardContent>
          </Card>
        )}

        {records.length === 0 && !showForm ? (
          <div className="text-center py-12">
            <Wrench className="h-10 w-10 text-neutral-200 mx-auto mb-3" />
            <p className="text-neutral-500 mb-1">No service records yet</p>
            {canManage && (
              <Button className="mt-4" onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                Create First Record
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Upcoming / in-progress */}
            {upcoming.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-2 px-1">Upcoming</p>
                <div className="space-y-2">
                  {upcoming.map(r => <ServiceCard key={r.id} record={r} equipmentId={eqId} canManage={canManage} />)}
                </div>
              </div>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-2 px-1">History</p>
                <div className="space-y-2">
                  {completed.map(r => <ServiceCard key={r.id} record={r} equipmentId={eqId} canManage={canManage} />)}
                </div>
              </div>
            )}
          </>
        )}

        <Button variant="outline" className="w-full" onClick={() => navigate(`/equipment/${eqId}`)}>
          Back to {equipment?.name ?? 'Equipment'}
        </Button>

      </div>
    </div>
  );
}

// ─── Service card ─────────────────────────────────────────────────────────────

function ServiceCard({ record, equipmentId, canManage }: { record: ServiceRecord; equipmentId: number; canManage: boolean }) {
  const orgTz = useOrgTimezone();
  const assignedName = [record.assigned_to_first_name, record.assigned_to_last_name].filter(Boolean).join(' ');

  const [expanded, setExpanded]       = useState(false);
  const [lines, setLines]             = useState<ServiceRecordPartLine[]>(record.parts_lines ?? []);
  const [loaded, setLoaded]           = useState((record.parts_lines ?? []).length > 0);
  const [totalCost, setTotalCost]     = useState<number>(Number(record.total_parts_cost || 0));

  async function loadLines() {
    const res = await equipmentService.getServiceRecordParts(equipmentId, record.id);
    if (res.success && res.data) {
      setLines(res.data);
      setTotalCost(res.data.reduce((s, l) => s + Number(l.total_cost || 0), 0));
    }
    setLoaded(true);
  }

  function handleToggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded) loadLines();
  }

  async function handleAddLine(line: { part_id?: number | null; description?: string; quantity: number; unit_cost?: number | null; unit?: string }) {
    const res = await equipmentService.addServiceRecordParts(equipmentId, record.id, [line]);
    if (res.success && res.data) {
      setLines(prev => [...prev, ...res.data.lines]);
      const newTotal = [...lines, ...res.data.lines].reduce((s, l) => s + Number(l.total_cost || 0), 0);
      setTotalCost(newTotal);
    }
  }

  async function handleRemoveLine(lineId: number) {
    await equipmentService.removeServiceRecordPart(equipmentId, record.id, lineId);
    const next = lines.filter(l => l.id !== lineId);
    setLines(next);
    setTotalCost(next.reduce((s, l) => s + Number(l.total_cost || 0), 0));
  }

  return (
    <div className="bg-white border border-neutral-100 rounded-xl px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', STATUS_COLOUR[record.status] ?? 'bg-neutral-100 text-neutral-600')}>
              {STATUS_LABEL[record.status] ?? record.status}
            </span>
          </div>
          <p className="font-medium text-neutral-800">{record.title}</p>
          {record.description && (
            <p className="text-sm text-neutral-500 mt-0.5">{record.description}</p>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
            {record.scheduled_date && (
              <span className="flex items-center gap-1 text-xs text-neutral-400">
                <Calendar className="h-3 w-3" />
                Scheduled {formatLocalDateInTz(record.scheduled_date, orgTz)}
              </span>
            )}
            {record.completed_at && (
              <span className="flex items-center gap-1 text-xs text-neutral-400">
                <CheckCircle className="h-3 w-3 text-green-500" />
                Completed {formatDateInTz(record.completed_at, orgTz)}
              </span>
            )}
            {record.hours_at_service != null && (
              <span className="flex items-center gap-1 text-xs text-neutral-400">
                <Gauge className="h-3 w-3" />
                At {record.hours_at_service} hrs
              </span>
            )}
            {assignedName && (
              <span className="text-xs text-neutral-400">Assigned to {assignedName}</span>
            )}
            {totalCost > 0 && (
              <span className="flex items-center gap-1 text-xs text-neutral-500 font-medium">
                <Package className="h-3 w-3" />
                Parts ${totalCost.toFixed(2)} ex GST
              </span>
            )}
          </div>
          {record.technician_notes && (
            <p className="text-xs text-neutral-500 mt-1.5 italic">"{record.technician_notes}"</p>
          )}
        </div>
        <button
          onClick={handleToggle}
          className="text-xs text-green-600 hover:text-green-700 font-medium flex-shrink-0"
        >
          {expanded ? 'Hide parts' : 'Parts'}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          {lines.length === 0 ? (
            <p className="text-xs text-neutral-400 mb-2">No parts recorded for this service.</p>
          ) : (
            <div className="space-y-1.5 mb-2">
              {lines.map(l => (
                <div key={l.id} className="flex items-center justify-between text-xs bg-neutral-50 rounded-lg px-2 py-1.5">
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-800 truncate">
                      {l.part_brand && l.part_number ? `${l.part_brand} · ${l.part_number}` : l.description ?? '—'}
                    </p>
                    <p className="text-neutral-500">
                      {l.quantity}{l.unit ? ` ${l.unit}` : ''} × ${Number(l.unit_cost || 0).toFixed(2)} = <span className="font-medium">${Number(l.total_cost || 0).toFixed(2)}</span> ex GST
                    </p>
                  </div>
                  {canManage && (
                    <button
                      onClick={() => handleRemoveLine(l.id)}
                      className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {canManage && <AddLineForm equipmentId={equipmentId} onAdd={handleAddLine} />}
        </div>
      )}
    </div>
  );
}

// ─── Add-part-line form ──────────────────────────────────────────────────────

function AddLineForm({ equipmentId, onAdd }: { equipmentId: number; onAdd: (line: { part_id?: number | null; description?: string; quantity: number; unit_cost?: number | null; unit?: string }) => Promise<void> }) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState<Part[]>([]);
  const [picked, setPicked]     = useState<Part | null>(null);
  const [description, setDesc]  = useState('');
  const [qty, setQty]           = useState('1');
  const [unitCost, setUnitCost] = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (!query || query.length < 2 || picked) { setResults([]); return; }
    const t = setTimeout(async () => {
      const res = await partsService.search(query, { equipment_id: equipmentId }, 1, 8);
      setResults(res.parts);
    }, 250);
    return () => clearTimeout(t);
  }, [query, picked, equipmentId]);

  async function handleSave() {
    if (!picked && !description.trim()) return;
    setSaving(true);
    try {
      await onAdd({
        part_id:     picked ? picked.id : null,
        description: picked ? undefined : description.trim(),
        quantity:    parseFloat(qty) || 1,
        unit_cost:   unitCost ? parseFloat(unitCost) : null,
        unit:        picked?.unit ?? undefined,
      });
      setPicked(null);
      setQuery('');
      setDesc('');
      setQty('1');
      setUnitCost('');
      setResults([]);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-neutral-50 rounded-lg p-2 space-y-2">
      <p className="text-[10px] text-neutral-500 uppercase tracking-wide">Add part</p>
      {picked ? (
        <div className="flex items-center justify-between bg-white rounded-md px-2 py-1.5 text-xs">
          <span className="font-medium text-neutral-800 truncate">{picked.brand} · {picked.part_number}</span>
          <button onClick={() => setPicked(null)} className="text-neutral-400 hover:text-neutral-600 ml-2">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search catalogue or type description…"
            className="w-full border border-neutral-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          {results.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {results.map(p => (
                <button
                  key={p.id}
                  onClick={() => { setPicked(p); setResults([]); setQuery(''); }}
                  className="block w-full text-left px-2 py-1.5 text-xs hover:bg-neutral-50 border-b border-neutral-100 last:border-0"
                >
                  <span className="font-medium text-neutral-800">{p.brand}</span> · {p.part_number}
                  {p.is_oem && <span className="ml-1 text-[10px] text-green-600">OEM</span>}
                </button>
              ))}
            </div>
          )}
          <input
            value={description}
            onChange={e => setDesc(e.target.value)}
            placeholder="…or free-text description"
            className="mt-1 w-full border border-neutral-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      )}
      <div className="grid grid-cols-3 gap-1.5">
        <input
          type="number" step="0.1" min="0" value={qty}
          onChange={e => setQty(e.target.value)} placeholder="Qty"
          className="border border-neutral-200 rounded-md px-2 py-1.5 text-xs"
        />
        <input
          type="number" step="0.01" min="0" value={unitCost}
          onChange={e => setUnitCost(e.target.value)} placeholder="Unit $ ex GST"
          className="border border-neutral-200 rounded-md px-2 py-1.5 text-xs"
        />
        <Button size="sm" isLoading={saving} onClick={handleSave} className="text-xs">
          <Plus className="h-3 w-3 mr-0.5" /> Add
        </Button>
      </div>
    </div>
  );
}
